import React, { FC } from "react"
import { graphql, PageProps } from "gatsby"
import MainLayout from "@layouts/MainLayout"
import Seo from "@components/Seo"
import components from "@components/strapi"
import type { ArticleContextProps } from "../types"

const ArticleTemplate: FC<PageProps<GatsbyTypes.articleQuery, ArticleContextProps>> = ({
    data
}) => {
    const { strapiArticle: article, relatedArticles } = data
    const sections = article?.sections
    const seo = article?.seo

    return <MainLayout>
        <Seo
            title={seo?.meta_title || article?.title}
            description={seo?.meta_description}
            index={seo?.index}
            metaImage={seo?.meta_image}
        />
        {sections && sections.map((section) => {
            if (!section) {
                return
            }

            const Component = components[section.__typename]
            let componentProps = { ...section }

            // When related news or repeatable component, pass along the related articles
            if (["StrapiComponentSectionsRelatedNews", "StrapiComponentSectionsComponent"].includes(`${section.__typename}`)) {
                componentProps = {
                    ...componentProps,
                    relatedArticles: relatedArticles?.nodes
                }
            }

            if (Component) {
                return <Component key={`${section.id}_${section.__typename}`} {...componentProps} />
            }

            return <React.Fragment key={`${section.id}_${section.__typename}`}>
                {process.env.NODE_ENV !== "production" ? `Missing section: ${section.__typename}` : ""}
            </React.Fragment>
        })}
    </MainLayout>
}

export default ArticleTemplate

export const articleQuery = graphql`
    query article($id: String!, $categoryId: String)
    {
        strapiArticle(id:{eq: $id}) {
            title
            slug
            seo {
                ...Seo
            }
            sections {
                ...RepeatableComponent
                ...Hero
                ...LatestNews
                ...RelatedNews
                ...SlidingText
                ...ImageSlider
                ...NewsletterSignup
                ...ContentHero
                ...Video
                ...Usps
                ...NewsContent
                ...Faq
            }
        }
        relatedArticles: allStrapiArticle (
            skip: 0
            limit: 3
            sort: {fields: published_date, order: DESC}
            filter: {category: {id: {eq: $categoryId}}}
        ) {
            nodes {
                slug
                publishedAt: published_date
                category {
                    title
                    icon {
                        alternativeText
                        file {
                            childImageSharp {
                                gatsbyImageData(
                                    height: 32
                                    layout: CONSTRAINED
                                )
                            }
                        }
                    }
                }
                thumbnail {
                    title
                    text
                    image {
                        file {
                            childImageSharp {
                                gatsbyImageData(
                                    width: 400
                                    height: 350
                                    quality: 80
                                    layout: CONSTRAINED
                                )
                            }
                        }
                    }
                }
            }
        }
    }
`
